import React, { useState, useEffect } from "react";
import { Button, Label } from "reactstrap";
import Select from "react-select";
import Creatable from "react-select/async-creatable";
import PropTypes from "prop-types";
import Form from "react-validation/build/form";
import Input from "react-validation/build/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import SUBSCRIPTION_METHODS from "src/utils/constants/subscriptionMethods";
import { GetErrorMessage } from "src/Pages/Report/Validation/validations";
import { restApiRequest, REPORT_SERVICE } from "src/utils/Api";
import isMockView from "src/utils/Api/isMockView";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import InputMask from "react-input-mask";
import Switch from "react-switch";
import DatePickerMax from "src/Components/FormElements/DatePickerMax";
import DefaultTooltip from "src/Components/Tooltips/DefaultTooltip";

const SubscriptionForm = ({
  clickSubscribe,
  isEdit,
  isSaving,
  isGetBlockBtn,
  isTokenAvailable,
  isdateOK,
  parametersState,
  parameters,
  companyId,
  subscriptionDto,
  setSubscriptionDto,
  isMultisheet,
}) => {
  const tokenValid = () => {
    if (isTokenAvailable === false) {
      return __(
        "wysyłka z zaszyfrowanym plikiem xls może nie zostać wysłana – token dostępowy jest pusty"
      );
    }
    return "";
  };

  const subScribeBtn = () => {
    const buttonText = isEdit ? __("Zapisz") : __("Ustaw");
    const buttonSavingText = isEdit ? __("Zapisywanie") : __("Ustawianie");

    return isSaving ? (
      <Button color="success" disabled>
        <FontAwesomeIcon icon={faSpinner} spin /> {buttonSavingText}{" "}
        {__("wysyłki...")}
      </Button>
    ) : (
      <Button className="mx-1" color="success" disabled={isGetBlockBtn}>
        {buttonText} {__("wysyłkę")}
      </Button>
    );
  };

  const fetchSubscriptionDto = async (companyId) => {
    const endpoint = `/v2/subscription/subscriptionoptions/${companyId}`;
    fetchData(
      (data) => {
        setSubscriptionDto({
          ...data,
          subscriptionOptions: {
            ...data.subscriptionOptions,
            parameters: parameters
              .filter((param) => param.typeForSubscription > 0)
              .map((param) => ({
                parameterName: param.parameterName,
                label: param.label,
                value: null,
                // eslint-disable-next-line
                typeForSubscription: param.typeForSubscription,
                periodType:
                  param.typeForSubscription === 1
                    ? { label: "Daty od-do", value: "2" }
                    : null,
                disabled: false,
              })),
          },
        });
      },
      endpoint,
      REPORT_SERVICE,
      "GET",
      {},
      [],
      __("Błąd podczas pobierania opcji subskrypcji")
    );
  };

  useEffect(() => {
    if (parameters.length > 0) fetchSubscriptionDto(companyId);
  }, [parameters]);

  const FrequencyType = {
    NONE: 0,
    MONTHLY: 1,
    WEEKLY: 4,
    DAILY: 5,
  };

  const handleFrequencyChange = (selectedOption) => {
    const { value: frequency } = selectedOption;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionFrequency: {
          ...prevOptions.subscriptionOptions.subscriptionFrequency,
          frequencyType: Number(frequency),
        },
        subscriptionSendingDays: {
          ...prevOptions.subscriptionOptions.subscriptionSendingDays,
          day: prevOptions.subscriptionOptions.subscriptionSendingDays.day,
        },
      },
    }));
  };

  const handleTitleChange = (e) => {
    const { value } = e.target;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionMail: {
          ...prevOptions.subscriptionOptions.subscriptionMail,
          title: value,
        },
      },
    }));
  };

  const handleMessageContentChange = (e) => {
    const { value } = e.target;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionMail: {
          ...prevOptions.subscriptionOptions.subscriptionMail,
          content: value,
        },
      },
    }));
  };

  const handleDynamicControlChange = (selectedOption, param) => {
    const { newValue, newPeriodType, newPeriodMonth, newPeriodDayOfMonth } =
      selectedOption;
    let isDisabled = false;
    let linkedParam = null;

    if (
      param.typeForSubscription === 1 &&
      param.parameterName.includes("StartDate")
    ) {
      linkedParam = subscriptionDto.subscriptionOptions.parameters.find(
        (p) =>
          p.parameterName ===
          param.parameterName.replace("StartDate", "EndDate")
      );
      // eslint-disable-next-line eqeqeq
      if (newPeriodType && newPeriodType.value == "1") {
        isDisabled = true;
      }
    }

    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        parameters: prevOptions.subscriptionOptions.parameters.map((p) => {
          if (p.parameterName === param.parameterName) {
            return {
              ...p,
              ...(newValue !== undefined && { value: newValue }),
              ...(newPeriodType !== undefined && { periodType: newPeriodType }),
              ...(newPeriodMonth !== undefined && {
                periodMonth: newPeriodMonth,
              }),
              ...(newPeriodDayOfMonth !== undefined && {
                periodDayOfMonth: newPeriodDayOfMonth,
              }),
            };
          }
          if (linkedParam && p.parameterName === linkedParam.parameterName) {
            return {
              ...p,
              ...(newPeriodType !== undefined && { periodType: newPeriodType }),
              disabled: isDisabled,
            };
          }
          return p;
        }),
      },
    }));
  };

  const handleDayChange = (selectedOption) => {
    const { value } = selectedOption;
    setSubscriptionDto((prevOptions) => {
      const { frequencyType } =
        prevOptions.subscriptionOptions.subscriptionFrequency;
      return {
        ...prevOptions,
        subscriptionOptions: {
          ...subscriptionDto.subscriptionOptions,
          subscriptionSendingDays: {
            ...prevOptions.subscriptionOptions.subscriptionSendingDays,
            ...(frequencyType === FrequencyType.WEEKLY
              ? { weeklySelectedValue: value }
              : { monthlySelectedValue: value }),
          },
        },
      };
    });
  };

  const handleLanguageChange = (selectedOption) => {
    const { value } = selectedOption;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionLanguage: {
          ...prevOptions.subscriptionOptions.subscriptionLanguage,
          languageSelectedValue: value,
        },
      },
    }));
  };

  const handleFormatChange = (selectedOption) => {
    const { value } = selectedOption;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionFileFormat: {
          ...prevOptions.subscriptionOptions.subscriptionFileFormat,
          fileFormatSelectedValue: value,
        },
      },
    }));
  };

  const handleSendingMethodChange = (selectedOption) => {
    const { value } = selectedOption;
    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionMethod: {
          ...prevOptions.subscriptionOptions.subscriptionMethod,
          sendingMethod: value,
        },
      },
    }));
  };

  const handleDayInputChange = (e, param) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = value;

    const intValue = parseInt(value, 10);
    if (value.length === 0 || (intValue > 0 && intValue <= 99)) {
      setSubscriptionDto((prevOptions) => ({
        ...prevOptions,
        subscriptionOptions: {
          ...subscriptionDto.subscriptionOptions,
          parameters: prevOptions.subscriptionOptions.parameters.map((p) =>
            p.parameterName === param.parameterName
              ? { ...p, previousDaysCount: intValue }
              : p
          ),
        },
      }));
    }
  };

  const handleBlurRecipients = (e) => {
    const data = e.target.value;
    const newData = { ...subscriptionDto };

    if (data !== "") {
      newData.subscriptionMail.recipients.push({ label: data, value: data });
      setSubscriptionDto(newData);
    }
  };

  const handleRecipientsChange = (data) => {
    let value = data;
    if (data.length === 0) {
      value = null;
    }

    setSubscriptionDto((prevOptions) => ({
      ...prevOptions,
      subscriptionOptions: {
        ...subscriptionDto.subscriptionOptions,
        subscriptionMail: {
          ...prevOptions.subscriptionOptions.subscriptionMail,
          recipients: value,
        },
      },
    }));
  };

  const renderDynamicControls = () => {
    // 1 - dla dat typu od / do + n dni
    // 2 - dla pojedynczych dat
    // 3 - rok i miesiac

    let tmpParameters = parameters.filter(
      (param) => param.typeForSubscription === 1
    );
    tmpParameters = tmpParameters.concat(
      parameters.filter(
        (param) =>
          param.typeForSubscription > 0 && param.typeForSubscription !== 1
      )
    );

    return tmpParameters.map((param, index) => {
      if (param.typeForSubscription === 1) {
        return (
          <div className="col-md-6" style={{ marginBottom: 10 }}>
            <div
              style={{
                boxShadow: "0 0 2px #aaa",
                padding: 10,
                borderRadius: 3,
              }}
            >
              <Label>
                {__(param.label)}
                {param.tooltipMessage && (
                  <span className="ml-1">
                    <DefaultTooltip
                      id={`${param.parameterName}-tooltip`}
                      content={__(param.tooltipMessage)}
                    />
                  </span>
                )}
              </Label>
              <div style={{ display: "flex", width: "100%", gap: 8 }}>
                {param.parameterName.includes("StartDate") && (
                  <div style={{ flexGrow: 1 }}>
                    <Label>{__("Typ zakresu")}</Label>
                    <Select
                      name={param.parameterName}
                      className={
                        !subscriptionDto.showInvalidFields ||
                        subscriptionDto.subscriptionOptions.parameters.find(
                          (p) => p.parameterName === param.parameterName
                        )?.periodType
                          ? "basic-select"
                          : "basic-select non-valid-parameter"
                      }
                      classNamePrefix="select"
                      value={
                        subscriptionDto.subscriptionOptions.parameters.find(
                          (p) => p.parameterName === param.parameterName
                        )?.periodType
                      }
                      options={Object.entries(
                        subscriptionDto.subscriptionOptions.subscriptionPeriod
                          .periodTypeListOptions
                      ).map(([key, value]) => ({
                        label: value,
                        value: key,
                      }))}
                      onChange={(selectedPeriodType) =>
                        handleDynamicControlChange(
                          { newPeriodType: selectedPeriodType },
                          param
                        )
                      }
                    />
                  </div>
                )}
                {subscriptionDto.subscriptionOptions.parameters.find(
                  (p) => p.parameterName === param.parameterName
                  // eslint-disable-next-line eqeqeq
                )?.periodType.value == 2 && (
                  <>
                    <div style={{ flexGrow: 1 }}>
                      <Label>{__("Miesiąc")}</Label>
                      <Select
                        value={
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.periodMonth
                        }
                        name={param.parameterName}
                        className={
                          !subscriptionDto.showInvalidFields ||
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.periodMonth
                            ? "basic-select"
                            : "basic-select non-valid-parameter"
                        }
                        classNamePrefix="select"
                        options={[
                          { label: __("Bieżący miesiąc"), value: 1 },
                          { label: __("Poprzedni miesiąc"), value: 2 },
                        ]}
                        onChange={(selectedMonth) =>
                          handleDynamicControlChange(
                            { newPeriodMonth: selectedMonth },
                            param
                          )
                        }
                        isDisabled={
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.disabled
                        }
                      />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                      <Label>{__("Dzień miesiąca")}</Label>
                      <Select
                        value={
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.periodDayOfMonth
                        }
                        name={param.parameterName}
                        className={
                          !subscriptionDto.showInvalidFields ||
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.periodDayOfMonth
                            ? "basic-select"
                            : "basic-select non-valid-parameter"
                        }
                        classNamePrefix="select"
                        options={Object.entries(
                          subscriptionDto.subscriptionOptions.subscriptionPeriod
                            .dayNumberListOptions
                        ).map(([key, value]) => ({
                          label: value,
                          value: key,
                        }))}
                        onChange={(selectedDay) =>
                          handleDynamicControlChange(
                            { newPeriodDayOfMonth: selectedDay },
                            param
                          )
                        }
                        isDisabled={
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.disabled
                        }
                      />
                    </div>
                  </>
                )}
                {subscriptionDto.subscriptionOptions.parameters.find(
                  (p) => p.parameterName === param.parameterName
                  // eslint-disable-next-line eqeqeq
                )?.periodType.value == 1 && (
                  <div style={{ flexGrow: 1 }}>
                    <Label>{__("Zakres")}</Label>
                    <div
                      className="input-group"
                      style={{
                        opacity:
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.disabled
                            ? 0.5
                            : 1,
                      }}
                    >
                      <div className="input-group-prepend">
                        <span className="input-group-text">ostatnie</span>
                      </div>
                      <InputMask
                        className={
                          !subscriptionDto.showInvalidFields ||
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.previousDaysCount ||
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.disabled
                            ? "form-control"
                            : "form-control non-valid-parameter"
                        }
                        type="text"
                        name={param.parameterName}
                        mask="99"
                        maskChar=""
                        alwaysShowMask={false}
                        onChange={(e) => handleDayInputChange(e, param)}
                        disabled={
                          subscriptionDto.subscriptionOptions.parameters.find(
                            (p) => p.parameterName === param.parameterName
                          )?.disabled
                        }
                      />
                      <div className="input-group-append">
                        <span className="input-group-text">dni</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {subscriptionDto.subscriptionOptions.parameters.find(
                (p) => p.parameterName === param.parameterName
                // eslint-disable-next-line eqeqeq
              )?.periodType.value == 2 &&
                subscriptionDto.subscriptionOptions.parameters.find(
                  (p) => p.parameterName === param.parameterName
                )?.periodDayOfMonth?.value >= 29 &&
                subscriptionDto.subscriptionOptions.parameters.find(
                  (p) => p.parameterName === param.parameterName
                )?.periodDayOfMonth?.value <= 31 && (
                  <div className="text-danger">
                    {__(
                      "Jeśli dzień wysyłki wykroczy poza liczbę dni w miesiącu, dniem wysyłki będzie ostatni dzień miesiąca."
                    )}
                  </div>
                )}
            </div>
          </div>
        );
      }
      if (param.typeForSubscription === 3) {
        return (
          <div className="col-md-3" style={{ marginBottom: 10 }}>
            <Label>{__(param.label)}</Label>
            <Select
              defaultValue={{ label: __("Wybierz..."), value: -1 }}
              name={param.parameterName}
              className="basic-select"
              classNamePrefix="select"
              options={[
                { label: __("Poprzedni miesiąc"), value: 0 },
                { label: __("Aktualny miesiąc"), value: -1 },
                { label: __("Następny miesiąc"), value: -2 },
              ]}
              onChange={(val) =>
                handleDynamicControlChange({ newValue: val }, param)
              }
            />
          </div>
        );
      }
      return null;
    });
  };

  const getDayLabel = () => {
    const { frequencyType } =
      subscriptionDto.subscriptionOptions.subscriptionFrequency;
    const selectedValue =
      frequencyType === FrequencyType.WEEKLY
        ? subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .weeklySelectedValue
        : subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .monthlySelectedValue;

    if (selectedValue === undefined) {
      return { label: __("Wybierz..."), value: -1 };
    }

    const label =
      frequencyType === FrequencyType.WEEKLY
        ? subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .weeklyListOptions?.[selectedValue]
        : subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .monthlyListOptions?.[selectedValue];

    return label
      ? { label, value: selectedValue }
      : { label: __("Wybierz..."), value: -1 };
  };

  const getDayOptions = () => {
    const { frequencyType } =
      subscriptionDto.subscriptionOptions.subscriptionFrequency || {};
    if (frequencyType === FrequencyType.WEEKLY) {
      return (
        subscriptionDto.subscriptionOptions.subscriptionSendingDays
          ?.weeklyListOptions &&
        Object.entries(
          subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .weeklyListOptions
        ).map(([key, value]) => ({
          label: value,
          value: key,
        }))
      );
      // eslint-disable-next-line
    } else if (frequencyType === FrequencyType.MONTHLY) {
      return (
        subscriptionDto.subscriptionOptions.subscriptionSendingDays
          ?.monthlyListOptions &&
        Object.entries(
          subscriptionDto.subscriptionOptions.subscriptionSendingDays
            .monthlyListOptions
        ).map(([key, value]) => ({
          label: value,
          value: key,
        }))
      );
    }
    return [];
  };

  const getFrequencyLabel = () => {
    if (
      !subscriptionDto.subscriptionOptions.subscriptionFrequency?.frequencyType
    ) {
      return { label: __("Wybierz..."), value: -1 };
    }

    const { frequencyType } =
      subscriptionDto.subscriptionOptions.subscriptionFrequency;
    const label =
      subscriptionDto.subscriptionOptions.subscriptionFrequency
        .frequencyListOptions[frequencyType];

    return label
      ? { label, value: frequencyType }
      : { label: __("Wybierz..."), value: -1 };
  };

  const getFrequencyOptions = () =>
    subscriptionDto.subscriptionOptions.subscriptionFrequency
      ?.frequencyListOptions &&
    Object.entries(
      subscriptionDto.subscriptionOptions.subscriptionFrequency
        .frequencyListOptions
    ).map(([key, value]) => ({
      label: value,
      value: key,
    }));

  const getLanguageLabel = () => {
    if (
      !subscriptionDto.subscriptionOptions.subscriptionLanguage
        ?.languageSelectedValue
    ) {
      return { label: __("Wybierz..."), value: -1 };
    }

    const { languageSelectedValue } =
      subscriptionDto.subscriptionOptions.subscriptionLanguage;
    const label =
      subscriptionDto.subscriptionOptions.subscriptionLanguage
        .languageListOptions[languageSelectedValue];

    return label
      ? { label, value: languageSelectedValue }
      : { label: __("Wybierz..."), value: -1 };
  };

  const getLanguageOptions = () =>
    subscriptionDto.subscriptionOptions.subscriptionLanguage
      ?.languageListOptions &&
    Object.entries(
      subscriptionDto.subscriptionOptions.subscriptionLanguage
        .languageListOptions
    ).map(([key, value]) => ({
      label: value,
      value: key,
    }));

  const getFormatLabel = () => {
    if (
      !subscriptionDto.subscriptionOptions.subscriptionFileFormat
        ?.fileFormatSelectedValue
    ) {
      return { label: __("Wybierz..."), value: -1 };
    }

    const { fileFormatSelectedValue } =
      subscriptionDto.subscriptionOptions.subscriptionFileFormat;
    const label =
      subscriptionDto.subscriptionOptions.subscriptionFileFormat
        .fileFormatListOptions[fileFormatSelectedValue];

    return label
      ? { label, value: fileFormatSelectedValue }
      : { label: __("Wybierz..."), value: -1 };
  };

  const getSendingMethodLabel = () => {
    if (
      !subscriptionDto.subscriptionOptions.subscriptionMethod
        ?.sendingMethodListOptions
    ) {
      return { label: __("Wybierz..."), value: -1 };
    }

    const { sendingMethod } =
      subscriptionDto.subscriptionOptions.subscriptionMethod;
    const label =
      subscriptionDto.subscriptionOptions.subscriptionMethod
        .sendingMethodListOptions[sendingMethod];

    return label
      ? { label, value: sendingMethod }
      : { label: __("Wybierz..."), value: -1 };
  };

  const getFormatOptions = () => {
    const allFormatOptions =
      subscriptionDto.subscriptionOptions.subscriptionFileFormat
        ?.fileFormatListOptions &&
      Object.entries(
        subscriptionDto.subscriptionOptions.subscriptionFileFormat
          .fileFormatListOptions
      ).map(([key, value]) => ({
        label: value,
        value: key,
      }));

    return isMultisheet
      ? allFormatOptions.filter((option) =>
          option.label.toLowerCase().includes("excel")
        )
      : allFormatOptions;
  };

  const getSendingMethodOptions = () =>
    subscriptionDto.subscriptionOptions.subscriptionMethod
      ?.sendingMethodListOptions &&
    Object.entries(
      subscriptionDto.subscriptionOptions.subscriptionMethod
        .sendingMethodListOptions
    ).map(([key, value]) => ({
      label: value,
      value: key,
    }));

  const getDayLabelText = () => {
    const { frequencyType } =
      subscriptionDto.subscriptionOptions.subscriptionFrequency;
    if (frequencyType === FrequencyType.WEEKLY) {
      return __("Dzień tygodnia");
      // eslint-disable-next-line
    } else if (frequencyType === FrequencyType.MONTHLY) {
      return __("Dzień miesiąca");
      // eslint-disable-next-line
    } else {
      return "";
    }
  };

  const getRecipientsOptions = () =>
    subscriptionDto.subscriptionOptions.subscriptionMail?.availableRecipients &&
    Object.entries(
      subscriptionDto.subscriptionOptions.subscriptionMail.availableRecipients
    ).map(([key, value]) => ({
      label: value,
      value: key,
    }));

  const getRecipientsLabel = () => {
    const recipients =
      subscriptionDto.subscriptionOptions.subscriptionMail?.recipients || [];
    return recipients.map((recipient) => ({
      label:
        subscriptionDto.subscriptionOptions.subscriptionMail
          .availableRecipients[recipient],
      value: recipient,
    }));
  };

  return (
    subscriptionDto && (
      <div className="col-md-12">
        <Form onSubmit={(e) => e.preventDefault()}>
          <div id="sub20">
            <div className="row">
              <div className="col-md-12">
                <h5>{__("Konfiguracja wysyłki")}</h5>
              </div>
            </div>
            <div className="row" style={{ marginBottom: 10 }}>
              <div className="col-md-3">
                <Label>{__("Częstotliwość")}</Label>
                <Select
                  defaultValue={{ label: __("Wybierz..."), value: -1 }}
                  value={getFrequencyLabel()}
                  name="frequency"
                  className="basic-select"
                  classNamePrefix="select"
                  options={getFrequencyOptions()}
                  onChange={handleFrequencyChange}
                  menuPlacement="auto"
                />
              </div>
              {subscriptionDto.subscriptionOptions.subscriptionFrequency
                .frequencyType !== FrequencyType.NONE &&
                subscriptionDto.subscriptionOptions.subscriptionFrequency
                  .frequencyType !== FrequencyType.DAILY && (
                  <div className="col-md-3">
                    <Label>{getDayLabelText()}</Label>
                    <Select
                      defaultValue={{ label: __("Wybierz..."), value: -1 }}
                      value={getDayLabel()}
                      name="day"
                      className="basic-select"
                      classNamePrefix="select"
                      options={getDayOptions()}
                      onChange={handleDayChange}
                    />
                  </div>
                )}
            </div>
            <div className="row">
              {renderDynamicControls()}
              <div className="col-md-3" style={{ marginBottom: 10 }}>
                <Label>{__("Język raportu")}</Label>
                <Select
                  defaultValue={{ label: __("Wybierz..."), value: -1 }}
                  value={getLanguageLabel()}
                  name="language"
                  className="basic-select"
                  classNamePrefix="select"
                  options={getLanguageOptions()}
                  onChange={handleLanguageChange}
                />
              </div>
            </div>
            <br />
            <div className="row">
              <div className="col-md-12">
                <h5>{__("Parametry wiadomości (mail)")}</h5>
              </div>
              <div className="col-md-12">
                <Label>{__("Adresaci")}</Label>
                <Select
                  isMulti
                  name="recipients"
                  className={
                    !subscriptionDto.showInvalidFields ||
                    subscriptionDto.subscriptionOptions.subscriptionMail
                      ?.recipients
                      ? "basic-select"
                      : "basic-select non-valid-parameter"
                  }
                  classNamePrefix="select"
                  onChange={handleRecipientsChange}
                  options={Object.entries(
                    subscriptionDto.subscriptionOptions.subscriptionMail
                      ?.availableRecipients
                  ).map(([key, mail]) => ({
                    label: mail,
                    value: key,
                  }))}
                  value={
                    subscriptionDto.subscriptionOptions.subscriptionMail
                      ?.recipients
                  }
                  placeholder={__("Wybierz...")}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <Label>{__("Format")}</Label>
                <Select
                  defaultValue={{ label: __("Wybierz..."), value: -1 }}
                  value={getFormatLabel()}
                  name="format"
                  className={
                    !subscriptionDto.showInvalidFields ||
                    subscriptionDto.subscriptionOptions.subscriptionFileFormat
                      ?.fileFormatSelectedValue
                      ? "basic-select"
                      : "basic-select non-valid-parameter"
                  }
                  classNamePrefix="select"
                  options={getFormatOptions()}
                  onChange={handleFormatChange}
                  menuPlacement="auto"
                />
              </div>
              <div className="col-md-6">
                <Label>{__("Sposób wysyłki")}</Label>
                <Select
                  defaultValue={{ label: __("Wybierz..."), value: -1 }}
                  value={getSendingMethodLabel()}
                  name="subscriptionMethod"
                  className="basic-select"
                  classNamePrefix="select"
                  options={getSendingMethodOptions()}
                  onChange={handleSendingMethodChange}
                />
              </div>
            </div>
          </div>{" "}
          <div className="col-md-12 text-danger">{tokenValid()}</div>
        </Form>
      </div>
    )
  );
};
SubscriptionForm.propTypes = {
  clickSubscribe: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isGetBlockBtn: PropTypes.bool.isRequired,
  isTokenAvailable: PropTypes.bool.isRequired,
  isdateOK: PropTypes.func.isRequired,
  parametersState: PropTypes.array.isRequired,
  parameters: PropTypes.array.isRequired,
  companyId: PropTypes.string.isRequired,
  subscriptionDto: PropTypes.object.isRequired,
  setSubscriptionDto: PropTypes.func.isRequired,
  isMultisheet: PropTypes.bool.isRequired,
};

SubscriptionForm.defaultProps = {};

export default SubscriptionForm;

const fetchData = async (
  updateData,
  endpoint,
  service,
  method,
  { headers, params, body },
  mockData,
  error
) => {
  if (isMockView()) {
    updateData(mockData);
  } else {
    try {
      const result = await restApiRequest(
        service,
        endpoint,
        method,
        { headers, params, body },
        {}
      );
      updateData(result);
    } catch (e) {
      console.error(e);
      newRelicErrorReport(
        e,
        "Pages/Report/NewReport/ReportsList/Report/Subscription/subscriptionForm.js - 353"
      );
      dynamicNotification(error || e.message || "błąd", "error");
    }
  }
};
